import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { format, parse, parseISO, isWithinInterval, isAfter } from 'date-fns'
import { groupBy } from 'lodash'

import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import { KeyboardDatePicker } from '@material-ui/pickers'
import Link from '@material-ui/core/Link'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import Badge from '@material-ui/core/Badge'

import { LayerSelector } from '../LayerSelector'
import {
	useDownloadTiffs,
	useTiffsHistory,
} from '@graniot/common/adapters/sentinel'
import GraniotSnackbar from '@graniot/common/utils/snackbar'

const useStyles = makeStyles((theme) => ({
	container: {
		marginTop: 20,
	},
	tab: {
		[theme.breakpoints.down('sm')]: {},
		[theme.breakpoints.up('md')]: {
			marginLeft: 100,
			marginRight: 100,
		},
		[theme.breakpoints.up('lg')]: {
			marginLeft: 250,
			marginRight: 250,
		},
	},
}))

const DownloadTiffs = ({
	layers,
	parcel,
	isLoadingParcelFISHistory,
	dataParcelFISHistory,
	selectedLayer,
	handleSelectedLayer,
}) => {
	const classes = useStyles()
	const { t } = useTranslation()
	const [openSnackbar, setOpenSnackbar] = React.useState(false)
	const [messageSnackbar, setMessageSnackbar] = React.useState('')
	const [statusSnackbar, setStatusSnackbar] = React.useState('error')
	const [period, setPeriod] = React.useState({
		startPeriod: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-LL-dd'),
		endPeriod: format(new Date(new Date().getFullYear(), 1, 25), 'yyyy-LL-dd'),
		years: [],
	})

	const [featureStats, setFeatureStats] = React.useState(null)
	const [imagesNumber, setImagesNumber] = React.useState(0)
	
	const {
		isLoading: isLoadingTiffsHistory,
		data: dataTiffsHistory,
		refetch: refetchTiffsHistory,
	} = useTiffsHistory(parcel.id)
	const years = groupBy(dataParcelFISHistory, (d) =>
		format(new Date(d.date), 'yyyy')
	)

	const mutateTiffs = useDownloadTiffs()

	const handleDownloadTiffs = () => {
		if (period.years.length === 0) {
			setMessageSnackbar(t('select_year'))
			setOpenSnackbar(true)
			return
		}
		mutateTiffs.mutate({ layer: selectedLayer, parcel, period })
	}

	

	React.useEffect(() => {
		if (dataParcelFISHistory) {
			let feature_stats = Object.assign([], dataParcelFISHistory)
			feature_stats.reverse()
			setFeatureStats(feature_stats)
		}
	}, [dataParcelFISHistory])

	React.useEffect(() => {
		if (
			Object.values(period).every((value) => value !== null) &&
			selectedLayer
		) {
			var tmpSum = 0
			period.years.forEach((year) => {
				const startDate = parseISO(period.startPeriod).setFullYear(year)
				const endDate = parseISO(period.endPeriod).setFullYear(year)
				if (isAfter(startDate, endDate)) {
					setMessageSnackbar(t('Invalid_Date'))
					setStatusSnackbar('error')
					setOpenSnackbar(true)
					return
				}
				tmpSum += dataParcelFISHistory?.filter((item) => {
					return isWithinInterval(parseISO(item.date), {
						start: startDate,
						end: endDate,
					})
				}).length
			})
			setImagesNumber(tmpSum)
		}
	}, [
		dataParcelFISHistory,
		selectedLayer,
		period.startPeriod,
		period.endPeriod,
		period.years,
	])

	React.useEffect(() => {
		refetchTiffsHistory()
	}, [mutateTiffs.isSuccess])

	return (
		<Container style={{ marginTop: 30 }}>
			<Grid container spacing={4}>
				<Grid item xs={3}>
					<LayerSelector
						layers={layers}
						selectedLayer={selectedLayer}
						handleSelectedLayer={handleSelectedLayer}
						width={'100%'}
					/>
				</Grid>
				<Grid item xs={2}>
					<KeyboardDatePicker
						label={t(`date_start`)}
						value={period.startPeriod}
						onChange={(newValue) =>
							setPeriod({
								...period,
								startPeriod: format(newValue, 'yyyy-MM-dd'),
							})
						}
						views={['month', 'date']}
						format="dd/MM"
						variant="inline"
						fullWidth
						autoOk
					/>
				</Grid>

				<Grid item xs={2}>
					<KeyboardDatePicker
						label={t(`date_end`)}
						value={period.endPeriod}
						onChange={(newValue) =>
							setPeriod({
								...period,
								endPeriod: format(newValue, 'yyyy-MM-dd'),
							})
						}
						views={['month', 'date']}
						format="dd/MM"
						variant="inline"
						fullWidth
						autoOk
					/>
				</Grid>
				<Grid item xs={3}>
					<Autocomplete
						loading={true}
						multiple
						id="tags-standard"
						value={period.years}
						onChange={(e, newValue) =>
							setPeriod({ ...period, years: newValue })
						}
						options={Object.keys(years)}
						getOptionLabel={(option) => option}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="standard"
								label={t('Years')}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{params.InputProps.endAdornment}
											{isLoadingParcelFISHistory && (
												<CircularProgress
													color="inherit"
													size={20}
												/>
											)}
										</>
									),
								}}
								InputLabelProps={{
									shrink: true,
								}}
								{...(isLoadingParcelFISHistory && { disabled: true })}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={2}>
					{mutateTiffs.isLoading ? (
						<CircularProgress color="inherit" size={20} />
					) : (
						<Badge badgeContent={imagesNumber} color="primary">
							<Button
								variant="outlined"
								color="primary"
								size="large"
								onClick={handleDownloadTiffs}
								fullWidth
							>
								{t('Download')}
							</Button>
						</Badge>
					)}
				</Grid>
				{mutateTiffs.data && (
					<Grid item xs={12}>
						<Link href={mutateTiffs.data.url}>
							{mutateTiffs.data.url}
						</Link>
					</Grid>
				)}
			</Grid>
			{dataTiffsHistory && (
				<TableContainer component={Paper} className={classes.container}>
					<Table className={classes.table} aria-label="simple table">
						<TableHead>
							<TableRow>
								<TableCell align="left">{t('Date')}</TableCell>
								<TableCell align="left">{t('From')}</TableCell>
								<TableCell align="left">{t('To')}</TableCell>
								<TableCell align="left">{t('Years')}</TableCell>
								<TableCell align="left">
									{t('number_of_images')}
								</TableCell>
								<TableCell align="left">Link</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{dataTiffsHistory.map((row) => (
								<TableRow key={row.name}>
									<TableCell align="left">
										{format(
											parseISO(row.created),
											'yyyy-MM-dd'
										)}
									</TableCell>
									<TableCell align="left">
										{format(
											parseISO(
												row.parameters.startPeriod
											),
											'yyyy-MM-dd'
										)}
									</TableCell>
									<TableCell align="left">
										{format(
											parseISO(row.parameters.endPeriod),
											'yyyy-MM-dd'
										)}
									</TableCell>
									<TableCell align="left">
										{row.parameters.years.join(' ')}
									</TableCell>
									<TableCell align="left">
										{row.number_of_images}
									</TableCell>
									<TableCell align="left">
										<Link href={row.link_s3}>
											<CloudDownloadIcon />
										</Link>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
			<GraniotSnackbar
				open={openSnackbar}
				setOpen={setOpenSnackbar}
				status={statusSnackbar}
				message={messageSnackbar}
			/>
		</Container>
	)
}

export default withWidth()(DownloadTiffs)
